import { useUserWithCompany } from '@app/hooks/useUserWithCompany';
import { Button, NotificationModal } from '@components/ui';
import { UserRole } from '@dieterApi/user/useUserQuery';
import {
  Checkbox,
  FormControl,
  FormControlLabel,
  FormGroup,
  FormLabel,
  ListItemText,
  MenuItem,
  ModalProps,
  Select,
  TextField,
} from '@mui/material';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

interface Props extends Omit<ModalProps, 'children' | 'onSubmit'> {
  onSubmit(values: { email?: string; role?: UserRole; enabledCompanies: string[] }): void;
}

function UserManagementCreateModal(props: Props) {
  const { t } = useTranslation();
  const { user } = useUserWithCompany();
  const { onSubmit, ...rest } = props;

  const [email, setEmail] = useState('');
  const [role, setRole] = useState<UserRole | ''>('');
  const [enabledCompanies, setEnabledCompanies] = useState<string[]>([]);
  const [all, setAll] = useState(false);
  const availableCompanies = user?.companies;

  // Admins have full access; a customer manager (Editor on a DpoManaged tenant)
  // may only assign the Editor / ReadOnly roles and cannot grant access to all
  // tenants at once.
  const isFullAccess = user?.role === UserRole.Admin;

  // The "Kunde" label is only shown to elevated viewers (Admin/Dpo); Editors and
  // read-only users see the Editor role labelled as "Bearbeiter".
  const viewerIsElevated = user?.role === UserRole.Admin || user?.role === UserRole.Dpo;

  const isAdmin = role === UserRole.Admin;

  useEffect(() => {
    if (all) {
      setEnabledCompanies(availableCompanies?.map((company) => company.id) || []);
    } else {
      setEnabledCompanies([]);
    }
  }, [all]);

  // Only Admin role forces all companies
  useEffect(() => {
    if (isAdmin) {
      setAll(true);
    }
  }, [isAdmin]);

  return (
    <NotificationModal {...rest}>
      <div className="flex flex-col gap-5 p-3 md:p-12" data-testid="user-invite-modal">
        <div className="h3">{t('route.user-management.create-modal.header', 'Invite new User')}</div>
        <div className="flex flex-col gap-5 w-full">
          <TextField
            className="w-full"
            name="email"
            label={t('route.user-management.table.header.email', 'Email')}
            variant="filled"
            size="small"
            onChange={(evt) => setEmail(evt.target.value)}
            value={email}
          />
          <TextField
            className="w-full"
            name="role"
            label={t('route.user-management.table.header.role', 'Role')}
            variant="filled"
            size="small"
            select
            value={role}
            onChange={(evt) => setRole(evt.target.value as unknown as UserRole)}
          >
            <MenuItem value={UserRole.Editor}>
              {viewerIsElevated
                ? t('route.user-management.role.Editor-dpo', 'Kunde')
                : t('route.user-management.role.Editor-self', 'Bearbeiter')}
            </MenuItem>
            {isFullAccess && (
              <MenuItem value={UserRole.Admin}>{t('route.user-management.role.Admin', 'Admin')}</MenuItem>
            )}
            {isFullAccess && user?.hasFeature('DpoManaged') && (
              <MenuItem value={UserRole.Dpo}>{t('route.user-management.role.Dpo', 'DSB')}</MenuItem>
            )}
            <MenuItem value={UserRole.ReadOnly}>{t('route.user-management.role.ReadOnly', 'ReadOnly')}</MenuItem>
          </TextField>

          <FormControl>
            <FormLabel component="legend">{t('route.user-management.access_to_tenants')}</FormLabel>
            <FormGroup row>
              <FormControlLabel
                label={t('common.all')}
                control={<Checkbox checked={all} onChange={(evt) => setAll(evt.target.checked)} disabled={isAdmin} />}
                sx={{ display: isFullAccess ? undefined : 'none' }}
              />

              {!all && (
                <Select
                  className="w-full"
                  name="enabledCompaniesList"
                  placeholder="Aktive Mandanten"
                  label="Aktive Mandanten"
                  variant="filled"
                  size="small"
                  multiple
                  value={enabledCompanies}
                  renderValue={(selected) =>
                    availableCompanies
                      ?.filter((c) => selected.includes(c.id))
                      .map((c) => c.name)
                      .join(', ')
                  }
                  onChange={(evt) => setEnabledCompanies(evt.target.value as unknown as string[])}
                >
                  {availableCompanies?.map((company) => {
                    return (
                      <MenuItem key={company.id} value={company.id}>
                        <Checkbox checked={enabledCompanies.indexOf(company.id) > -1} />
                        <ListItemText primary={company.name} />
                      </MenuItem>
                    );
                  })}
                </Select>
              )}
            </FormGroup>
            {all && (
              <p className="text-sm text-red-600 mt-2">
                {t(
                  'route.user-management.access_all_warning',
                  'Achtung: Der Benutzer erhält Zugriff auf alle Mandanten.'
                )}
              </p>
            )}
          </FormControl>
        </div>
        <div>
          <Button
            onClick={() => onSubmit({ email, role: role as UserRole, enabledCompanies })}
            disabled={!email || !role || !enabledCompanies.length}
          >
            {t('common.invite', 'invite')}
          </Button>
        </div>
      </div>
    </NotificationModal>
  );
}

export default UserManagementCreateModal;
